import React from "react";
import * as Icon from "react-feather";
import moment from "moment";
import clsx from "clsx";
import BookkeepingService from "../../../../../../services/BookkeepingService";
import WComponent from "../../../../../common/WComponent";
import "./YearEndPackageView.css";

const SYSTEM_FILES = [
	{ docType: "master",   label: "0. <Client> YE Package (Master)", description: "Single combined PDF that concatenates all individual letters and statements — what the client signs." },
	{ docType: "cel",      label: "1. Client Enclosure Letter",       description: "Cover letter to the client describing what is enclosed and asking for signature." },
	{ docType: "fs",       label: "2. Financial Statements",          description: "Compilation report + Balance Sheet + Income Statement + Notes (frozen at sign-off)." },
	{ docType: "ml",       label: "3. Management Responsibility Letter", description: "Standard attestation signed by the client confirming management's responsibility for the financial information." },
	{ docType: "ll",       label: "5. Letter to the Lawyer",          description: "Standard CSRS request to the client's lawyer — only generated when lawyer info is provided below." },
	{ docType: "tb",       label: "6. Trial Balance",                 description: "Post-AJE canonical trial balance grouped by rollup." },
	{ docType: "aje",      label: "7. Adjusting Journal Entries",     description: "All accepted AJEs with debit/credit detail." },
	{ docType: "manifest", label: "9. Year-End Package Manifest",     description: "Internal record — engagement, sign-offs, AJE totals, cross-check pass/fail." },
];

function formatTimestamp(value) {
	if (!value) return "—";
	return moment(value).format("MMM D, YYYY h:mm A");
}

function formatBytes(bytes) {
	if (!bytes) return "—";
	if (bytes < 1024) return bytes + " B";
	if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + " KB";
	return (bytes / 1024 / 1024).toFixed(2) + " MB";
}

class YearEndPackageView extends WComponent {
	// `pkgState` holds the loaded package state — renamed from the old `state`
	// useState var to avoid clashing with the class's own this.state.
	state = {
		loading: true,
		pkgState: null,
		attachable: [],
		selectedIds: {},
		targetFolderId: "",
		busy: false,
		errorMessage: null,
		lastResult: null,
		forceMode: false,
		lawyerName: "",
		lawyerFirm: "",
		lawyerEmail: "",
		lawyerAddress: "",
		excludedDocs: {},
	};

	componentDidMount() {
		this.loadState();
	}

	componentDidUpdate(prevProps) {
		if (prevProps.clientId !== this.props.clientId) {
			this.loadState();
		}
	}

	loadState = async () => {
		this.update({ loading: true });
		let data = await BookkeepingService.loadYearEndPackage({ clientId: this.props.clientId });
		if (data) {
			this.update({
				pkgState: {
					state: data.state,
					package_built_at: data.package_built_at,
					package_built_by: data.package_built_by,
					last_event: data.last_event,
				},
				attachable: data.attachable_documents || [],
				lawyerName: data.lawyer_name || "",
				lawyerFirm: data.lawyer_firm || "",
				lawyerEmail: data.lawyer_email || "",
				lawyerAddress: data.lawyer_address || "",
			});
		}
		this.update({ loading: false });
	};

	toggleDoc = (id) => {
		this.setState(function (prev) {
			let next = Object.assign({}, prev.selectedIds);
			if (next[id]) delete next[id]; else next[id] = true;
			return { selectedIds: next };
		});
	};

	toggleDocExclusion = (docType) => {
		this.setState(function (prev) {
			let next = Object.assign({}, prev.excludedDocs);
			if (next[docType]) delete next[docType]; else next[docType] = true;
			return { excludedDocs: next };
		});
	};

	buildPayload = () => {
		let { selectedIds, excludedDocs, targetFolderId, forceMode, lawyerName, lawyerFirm, lawyerEmail, lawyerAddress, pkgState } = this.state;
		let currentState = (pkgState && pkgState.state) || this.props.engagementState || null;
		let isFinalized = currentState === "FINALIZED";
		return {
			clientId: this.props.clientId,
			attachedDocumentIds: Object.keys(selectedIds).map(Number),
			targetFolderId: targetFolderId || null,
			force: !isFinalized && forceMode,
			lawyerName: lawyerName,
			lawyerFirm: lawyerFirm,
			lawyerEmail: lawyerEmail,
			lawyerAddress: lawyerAddress,
			excludedDocTypes: Object.keys(excludedDocs).filter(function (k) { return excludedDocs[k]; }),
		};
	};

	onBuild = async () => {
		this.update({ busy: true, errorMessage: null });
		let result = await BookkeepingService.buildYearEndPackage(this.buildPayload());
		if (result && result.error) {
			this.update({ errorMessage: (result.error.message || "Build failed") + (result.error.current_state ? " (state: " + result.error.current_state + ")" : "") });
		} else {
			this.update({ lastResult: result });
			await this.loadState();
		}
		this.update({ busy: false });
	};

	onDownloadZip = async () => {
		this.update({ busy: true, errorMessage: null });
		try {
			await BookkeepingService.downloadYearEndPackageZip(this.buildPayload());
			await this.loadState();
		} catch (err) {
			this.update({ errorMessage: err.message || "Download failed" });
		}
		this.update({ busy: false });
	};

	render() {
		let { engagementState } = this.props;
		let {
			loading, pkgState, attachable, selectedIds, targetFolderId, busy, errorMessage, lastResult,
			forceMode, lawyerName, lawyerFirm, lawyerEmail, lawyerAddress, excludedDocs,
		} = this.state;

		let currentState = (pkgState && pkgState.state) || engagementState || null;
		let isFinalized = currentState === "FINALIZED";
		let canBuild = isFinalized || forceMode;

		let attachedDocumentIds = Object.keys(selectedIds).map(Number);
		let excludedDocTypes = Object.keys(excludedDocs).filter(function (k) { return excludedDocs[k]; });

		if (loading) {
			return <div className="ye-package"><div className="ye-package__loading">Loading year-end package state…</div></div>;
		}

		let attachedCount = attachedDocumentIds.length;

		return (
			<div className="ye-package">
				<div className="ye-package__header">
					<span className="ye-package__title">Year-End Package</span>
					{pkgState && pkgState.package_built_at ? (
						<span className="ye-package__last-built">
							<Icon.CheckCircle size={13} />
							Last built {formatTimestamp(pkgState.package_built_at)} by user #{pkgState.package_built_by}
						</span>
					) : (
						<span className="ye-package__last-built ye-package__last-built--never">Not yet built</span>
					)}
				</div>

				{!isFinalized && (
					<div className={clsx("ye-package__notice", forceMode && "ye-package__notice--warn")}>
						{forceMode ? <Icon.AlertTriangle size={14} /> : <Icon.Lock size={14} />}
						<span>
							{forceMode
								? "Force/preview mode is on — building from live (unsigned) state. The PDFs will be watermarked as PREVIEW."
								: "The engagement must be sign-off finalized before the year-end package can be built."}
						</span>
						<span className="ye-package__state-chip">{currentState || "—"}</span>
						<label className="ye-package__force-toggle">
							<input type="checkbox" checked={forceMode} onChange={(e) => this.update({ forceMode: e.target.checked })} />
							Bypass for testing
						</label>
					</div>
				)}

				<div className="ye-package__section">
					<div className="ye-package__section-title">
						System-generated PDFs
						<span className="ye-package__count">{SYSTEM_FILES.length - excludedDocTypes.length} of {SYSTEM_FILES.length} selected</span>
					</div>
					<ul className="ye-package__system-list">
						{SYSTEM_FILES.map((f) => {
							let isExcluded = !!excludedDocs[f.docType];
							let llDisabled = f.docType === "ll" && !(lawyerName && lawyerFirm);
							let effectivelyOff = isExcluded || llDisabled;
							return (
								<li
									key={f.docType}
									className={clsx(
										"ye-package__system-row",
										effectivelyOff && "ye-package__system-row--off",
										llDisabled && "ye-package__system-row--locked"
									)}
									onClick={() => { if (!llDisabled) this.toggleDocExclusion(f.docType); }}
								>
									<input
										type="checkbox"
										className="ye-package__system-check"
										checked={!effectivelyOff}
										disabled={llDisabled}
										readOnly
									/>
									<div className="ye-package__system-body">
										<div className="ye-package__system-name">{f.label}</div>
										<div className="ye-package__system-desc">
											{f.description}
											{llDisabled && <span className="ye-package__system-locked-hint"> — fill lawyer name and firm below to enable</span>}
										</div>
									</div>
								</li>
							);
						})}
					</ul>
				</div>

				<div className="ye-package__section">
					<div className="ye-package__section-title">
						Attach supporting documents
						{attachable.length > 0 && <span className="ye-package__count">{attachedCount} of {attachable.length} selected</span>}
					</div>
					{attachable.length === 0 ? (
						<div className="ye-package__empty">No supporting documents uploaded yet for this engagement.</div>
					) : (
						<ul className="ye-package__doc-list">
							{attachable.map((d) => {
								let isOn = !!selectedIds[d.id];
								return (
									<li key={d.id} className={clsx("ye-package__doc-row", isOn && "ye-package__doc-row--selected")} onClick={() => this.toggleDoc(d.id)}>
										<input type="checkbox" checked={isOn} readOnly className="ye-package__doc-check" />
										<div className="ye-package__doc-name">{d.name}{d.extension ? "." + d.extension : ""}</div>
										<div className="ye-package__doc-meta">
											{d.evidence_requirement_code && <span className="ye-package__doc-code">{d.evidence_requirement_code}</span>}
											{d.wp_ref && <span className="ye-package__doc-wpref">{d.wp_ref}</span>}
											<span className="ye-package__doc-size">{formatBytes(d.size)}</span>
											<span className="ye-package__doc-date">{formatTimestamp(d.uploaded_at)}</span>
										</div>
									</li>
								);
							})}
						</ul>
					)}
				</div>

				<div className="ye-package__section">
					<div className="ye-package__section-title">
						Letter to the Lawyer
						{lawyerName && lawyerFirm
							? <span className="ye-package__count ye-package__count--ok">LL will be generated</span>
							: <span className="ye-package__count">LL skipped — fill name and firm to enable</span>
						}
					</div>
					<div className="ye-package__lawyer-grid">
						<div className="ye-package__field-row">
							<label className="ye-package__label" htmlFor="ye-package-lawyer-name">Lawyer name</label>
							<input
								id="ye-package-lawyer-name"
								className="ye-package__input"
								type="text"
								placeholder="e.g. Jane Smith"
								value={lawyerName}
								onChange={(e) => this.update({ lawyerName: e.target.value })}
							/>
						</div>
						<div className="ye-package__field-row">
							<label className="ye-package__label" htmlFor="ye-package-lawyer-firm">Lawyer's firm</label>
							<input
								id="ye-package-lawyer-firm"
								className="ye-package__input"
								type="text"
								placeholder="e.g. Smith Howell LLP"
								value={lawyerFirm}
								onChange={(e) => this.update({ lawyerFirm: e.target.value })}
							/>
						</div>
						<div className="ye-package__field-row">
							<label className="ye-package__label" htmlFor="ye-package-lawyer-email">Lawyer's email</label>
							<input
								id="ye-package-lawyer-email"
								className="ye-package__input"
								type="email"
								placeholder="optional"
								value={lawyerEmail}
								onChange={(e) => this.update({ lawyerEmail: e.target.value })}
							/>
						</div>
						<div className="ye-package__field-row ye-package__field-row--multiline">
							<label className="ye-package__label" htmlFor="ye-package-lawyer-address">Lawyer's address</label>
							<textarea
								id="ye-package-lawyer-address"
								className="ye-package__input ye-package__input--multiline"
								rows={3}
								placeholder="Street, City, Province, Postal Code"
								value={lawyerAddress}
								onChange={(e) => this.update({ lawyerAddress: e.target.value })}
							/>
						</div>
					</div>
					<div className="ye-package__field-help">
						Saved on the engagement when you build — fill once and it persists for rebuilds. Empty fields skip Letter-to-the-Lawyer generation entirely.
					</div>
				</div>

				<div className="ye-package__section">
					<div className="ye-package__section-title">Destination (optional)</div>
					<div className="ye-package__field-row">
						<label className="ye-package__label" htmlFor="ye-package-folder">Google Drive folder ID</label>
						<input
							id="ye-package-folder"
							className="ye-package__input"
							type="text"
							placeholder="Leave blank to skip Drive upload and just download a zip"
							value={targetFolderId}
							onChange={(e) => this.update({ targetFolderId: e.target.value })}
						/>
					</div>
					<div className="ye-package__field-help">
						When provided, the bundle is uploaded under <code>FINAL / To Sign</code> beneath this folder. Per-client folder routing is a separate workstream — for now paste a folder ID manually, or leave blank and download a zip.
					</div>
				</div>

				{errorMessage && (
					<div className="ye-package__error">
						<Icon.AlertCircle size={14} />
						<span>{errorMessage}</span>
					</div>
				)}

				<div className="ye-package__actions">
					<button className="ye-package__btn ye-package__btn--primary" onClick={this.onBuild} disabled={busy || !canBuild || !targetFolderId}>
						<Icon.UploadCloud size={14} />
						{busy ? "Working…" : "Build & Upload to Drive"}
					</button>
					<button className="ye-package__btn" onClick={this.onDownloadZip} disabled={busy || !canBuild}>
						<Icon.Download size={14} />
						{busy ? "Working…" : "Build & Download Zip"}
					</button>
				</div>

				{lastResult && lastResult.files && (
					<div className="ye-package__section">
						<div className="ye-package__section-title">Last build output</div>
						<ul className="ye-package__output-list">
							{lastResult.files.map(function (f, i) {
								return (
									<li key={i} className="ye-package__output-row">
										<Icon.File size={13} />
										<span className="ye-package__output-name">{f.suggested_name}</span>
										<span className="ye-package__output-size">{formatBytes(f.size)}</span>
									</li>
								);
							})}
						</ul>
						{lastResult.drive && lastResult.drive.folder_id && (
							<div className="ye-package__field-help">
								Uploaded to Drive folder <code>{lastResult.drive.folder_id}</code>.
							</div>
						)}
						{lastResult.summary && (
							<div className="ye-package__summary">
								<span>AJEs: {lastResult.summary.aje_count}</span>
								<span>Attached: {lastResult.summary.attached_count}</span>
								<span>Cross-checks: {lastResult.summary.cross_check_pass} pass / {lastResult.summary.cross_check_fail} fail</span>
							</div>
						)}
					</div>
				)}
			</div>
		);
	}
}

export default YearEndPackageView;
